import { formatDate } from 'src/helpers/Date';
import type {
  PurchasePolicyAdditionalData,
  PurchasePolicyInput,
} from '../../redux/reducer/v2/purchasePolicy';
import type { ErrorListOutput } from '../../types';

export interface UpdateApplicationSuccessResponse {
  updateApplication: {
    id: string;
    buildingId: string;
  };
}

export interface UpdateApplicationErrorResponse {
  updateApplication: ErrorListOutput & {
    id: never;
    buildingId: never;
  };
}

export type UpdateApplicationResponse =
  | UpdateApplicationSuccessResponse
  | UpdateApplicationErrorResponse;

type PickedPurchasePolicyInputForUpdateApplication = Pick<
  PurchasePolicyInput,
  | 'coverageStart'
  | 'buildingId'
  | 'buildingName'
  | 'address'
  | 'additionalInsuredRoommates'
  | 'additionalInterests'
  | 'firstName'
  | 'lastName'
  | 'email'
  | 'buildingUnit'
  | 'phone'
>;

type PickedPurchasePolicyAdditionalDataForUpdateApplication = Pick<
  PurchasePolicyAdditionalData,
  'id' | 'communityId'
>;

export type UpdateApplicationAdditionalData =
  PickedPurchasePolicyAdditionalDataForUpdateApplication;

export function getBuildingQueryString({
  address,
  buildingId,
  buildingName,
}: {
  buildingId: string | undefined;
  buildingName: string | undefined;
  address: PickedPurchasePolicyInputForUpdateApplication['address'] | undefined;
}) {
  if (buildingId) {
    return `buildingId: "${buildingId}",`;
  }

  if (buildingName) {
    return `buildingName: "${buildingName}",`;
  }

  if (address) {
    return `address: {
      street: "${address.street}",
      shortNameStreet: "${address.shortNameStreet}",
      municipality:  "${address.municipality}",
      addressNumber:  "${address.addressNumber}",
      region: ${address.state},
      postalCode:  "${address.postalCode}",
    },`;
  }
  return '';
}

export function formatAdditionalPerson(
  person: PickedPurchasePolicyInputForUpdateApplication['additionalInsuredRoommates'][number],
) {
  return `{
    firstName: "${person.firstName}",
    lastName: "${person.lastName}",
    email: "${person.email}"
  }`;
}

function getAdditionalInsuredRoommatesQueryString(
  additionalInsuredRoommates?:
    | PickedPurchasePolicyInputForUpdateApplication['additionalInsuredRoommates'],
) {
  if (
    !Array.isArray(additionalInsuredRoommates) ||
    additionalInsuredRoommates.length === 0
  ) {
    return '';
  }

  const queryString = `additionalInsuredRoommates: [${additionalInsuredRoommates
    .map((person) => formatAdditionalPerson(person))
    .join(',')}],`;

  return queryString;
}

function getAdditionalInterestsQueryString(
  additionalInterests?: PickedPurchasePolicyInputForUpdateApplication['additionalInterests'],
) {
  if (!Array.isArray(additionalInterests) || additionalInterests.length === 0) {
    return '';
  }

  const queryString = `additionalInterests: [${additionalInterests
    .map((person) => formatAdditionalPerson(person))
    .join(',')}],`;

  return queryString;
}

export const UPDATE_APPLICATION = (
  application: Partial<PickedPurchasePolicyInputForUpdateApplication>,
  additionalData: UpdateApplicationAdditionalData,
) => {
  const formattedCoverageStart = application.coverageStart
    ? formatDate(application.coverageStart)
    : null;
  const coverageStart = formattedCoverageStart
    ? `coverageStart: "${formattedCoverageStart}",`
    : '';
  const communityId = additionalData.communityId
    ? `communityId: "${additionalData.communityId}",`
    : '';
  const buildingQueryString = getBuildingQueryString({
    address: application.address,
    buildingId: application.buildingId,
    buildingName: application.buildingName,
  });
  const additionalInsuredRoommatesQueryString =
    getAdditionalInsuredRoommatesQueryString(
      application.additionalInsuredRoommates,
    );
  const additionalInterestsQueryString = getAdditionalInterestsQueryString(
    application.additionalInterests,
  );
  const firstName = application.firstName
    ? `firstName: "${application.firstName}",`
    : '';
  const lastName = application.lastName
    ? `lastName: "${application.lastName}",`
    : '';
  const email = application.email ? `email: "${application.email}",` : '';
  const buildingUnit = application.buildingUnit
    ? `buildingUnit: "${application.buildingUnit}",`
    : '';
  const phone = application.phone ? `phone: "${application.phone}",` : '';

  // TODO: leaseEnd?
  // if (application.leaseEnd) {
  //   const formattedLeaseEnd = formatDate(application.leaseEnd);
  //   mutation += formattedLeaseEnd ? `leaseEnd:"${formattedLeaseEnd}",` : '';
  // }

  return `mutation {
    updateApplication(
      applicationId: "${additionalData.id}"
      input: {
        ${coverageStart}
        ${communityId}
        ${buildingQueryString}
        ${additionalInsuredRoommatesQueryString}
        ${additionalInterestsQueryString}
        ${firstName}
        ${lastName}
        ${email}
        ${buildingUnit}
        ${phone}
      }
    ) {
      ... on ApplicationOutput {
        id
        buildingId
      }
      ... on ErrorListOutput {
        errors {
          type
          message
        }
      }
    }
  }`;
};
